import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Card,
  EmptyState,
  Filters,
  Page,
  Pagination,
  ResourceItem,
  ResourceList,
  Select,
  Spinner,
  Stack,
  TextStyle,
  Thumbnail,
} from "@shopify/polaris";
import { useAdmin } from "context/admin";
import { fetcher } from "util/fetch";

const STATUS_BADGE = {
  active: "success",
  draft: "attention",
  archived: "critical",
};

const tagCount = (tags) => {
  if (!tags) return 0;
  return typeof tags === "string"
    ? tags.split(",").filter((t) => t.trim()).length
    : 0;
};

export default function ProductCatalogPage() {
  const { selectedShopId, setToast } = useAdmin();
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [productTypeFilter, setProductTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const perPage = 20;

  const fetchProductTypes = useCallback(() => {
    if (!selectedShopId) return;
    fetcher(`/api/shops/${selectedShopId}/products/types`)
      .then((res) => res.json())
      .then(setProductTypes)
      .catch(() => setToast("Error loading product types."));
  }, [selectedShopId, setToast]);

  const fetchProducts = useCallback(() => {
    if (!selectedShopId) return;
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("per_page", perPage);
    params.set("status", statusFilter || "active");
    if (query.trim()) params.set("query", query.trim());
    if (productTypeFilter)
      params.set("filter", "type:" + encodeURIComponent(productTypeFilter));

    fetcher("/api/shops/" + selectedShopId + "/products?" + params)
      .then((res) => res.json())
      .then((data) => {
        const items = Array.isArray(data) ? data : data.products || [];
        setProducts(items);
        setHasMore(items.length === perPage);
      })
      .catch(() => setToast("Error loading products."))
      .finally(() => setLoading(false));
  }, [selectedShopId, page, statusFilter, query, productTypeFilter, setToast]);

  useEffect(() => {
    fetchProductTypes();
  }, [fetchProductTypes]);

  useEffect(() => {
    setPage(1);
  }, [query, productTypeFilter, statusFilter]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleClearFilters = () => {
    setProductTypeFilter("");
    setStatusFilter("active");
    setQuery("");
    setPage(1);
  };

  const filterConfig = [
    {
      key: "productType",
      label: "Product Type",
      filter: (
        <Select
          label="Product Type"
          labelHidden
          options={[
            { label: "All types", value: "" },
            ...productTypes.map((t) => ({ label: t || "(blank)", value: t })),
          ]}
          value={productTypeFilter}
          onChange={setProductTypeFilter}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = [];
  if (productTypeFilter) {
    appliedFilters.push({
      key: "productType",
      label: "Type: " + productTypeFilter,
      onRemove: () => setProductTypeFilter(""),
    });
  }

  const resourceName = { singular: "product", plural: "products" };

  const renderItem = (product) => {
    const media = product.image && product.image.src ? (
      <Thumbnail source={product.image.src} alt={product.title} size="small" />
    ) : (
      <Thumbnail source="" alt="No image" size="small" />
    );

    return (
      <ResourceItem
        id={String(product.product_id)}
        media={media}
        accessibilityLabel={"View details for " + product.title}
      >
        <Stack distribution="equalSpacing" alignment="center">
          <Stack.Item fill>
            <h3>
              <TextStyle variation="strong">{product.title}</TextStyle>
            </h3>
            <div style={{ marginTop: "0.25rem" }}>
              <TextStyle variation="subdued">
                {[product.vendor, product.product_type]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </TextStyle>
            </div>
          </Stack.Item>
          <Stack.Item>
            <Stack spacing="tight">
              <Badge status={STATUS_BADGE[product.status] || "default"}>
                {product.status || "—"}
              </Badge>
              <TextStyle variation="subdued">
                {tagCount(product.tags)} tag{tagCount(product.tags) !== 1 ? "s" : ""}
              </TextStyle>
            </Stack>
          </Stack.Item>
        </Stack>
      </ResourceItem>
    );
  };

  const emptyState =
    !loading && products.length === 0 ? (
      <EmptyState
        heading="No products found"
        image=""
        action={{ content: "Clear filters", onAction: handleClearFilters }}
      >
        <p>Try adjusting your search or filters.</p>
      </EmptyState>
    ) : null;

  return (
    <Page title="Product Catalog">
      <Card>
        <div style={{ padding: "1rem 1rem 0" }}>
          <Stack alignment="trailing" spacing="tight">
            <Stack.Item fill>
              <Filters
                queryValue={query}
                queryPlaceholder="Search by title, SKU, or barcode"
                filters={filterConfig}
                appliedFilters={appliedFilters}
                onQueryChange={setQuery}
                onQueryClear={() => setQuery("")}
                onClearAll={handleClearFilters}
              />
            </Stack.Item>
            <Select
              label="Status"
              labelInline
              options={[
                { label: "Active", value: "active" },
                { label: "Draft", value: "draft" },
                { label: "Archived", value: "archived" },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
            />
          </Stack>
        </div>

        {loading ? (
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spinner size="large" />
          </div>
        ) : emptyState ? (
          emptyState
        ) : (
          <ResourceList
            resourceName={resourceName}
            items={products}
            renderItem={renderItem}
          />
        )}

        {!loading && products.length > 0 && (page > 1 || hasMore) && (
          <div style={{ padding: "1rem" }}>
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage((p) => Math.max(1, p - 1))}
              hasNext={hasMore}
              onNext={() => setPage((p) => p + 1)}
              label={"Page " + page}
            />
          </div>
        )}
      </Card>
    </Page>
  );
}
